import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { FileText, Plus, X, Save, Trash2, Edit2, Eye, EyeOff, Image, Tag, Calendar, User } from 'lucide-react'
import { db } from '../../firebase'
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, serverTimestamp, query, orderBy } from 'firebase/firestore'
import { formatDate } from '../../utils/helpers.jsx'
import ConfirmModal from '../ConfirmModal'

const BlogCMS = () => {
  const [posts, setPosts] = useState([])
  const [showEditor, setShowEditor] = useState(false)
  const [editingPost, setEditingPost] = useState(null)
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, postId: null })
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: '',
    tags: '',
    image: '',
    author: 'Contenta Team',
    published: false,
    featured: false
  })

  useEffect(() => {
    const postsRef = collection(db, 'blogPosts')
    const q = query(postsRef, orderBy('createdAt', 'desc'))
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const loaded = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate()
      }))
      setPosts(loaded)
    })

    return () => unsubscribe()
  }, [])

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[ë]/g, 'e')
      .replace(/[ç]/g, 'c')
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/--+/g, '-')
      .trim()
  }

  const handleTitleChange = (title) => {
    setFormData({
      ...formData,
      title,
      slug: generateSlug(title)
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    try {
      const postData = {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(t => t),
        readTime: Math.ceil(formData.content.split(' ').length / 200), // ~200 words per minute
      }

      if (editingPost) {
        await updateDoc(doc(db, 'blogPosts', editingPost.id), {
          ...postData,
          updatedAt: serverTimestamp()
        })
      } else {
        await addDoc(collection(db, 'blogPosts'), {
          ...postData,
          createdAt: serverTimestamp(),
          views: 0,
          likes: 0
        })
      }

      setShowEditor(false)
      setEditingPost(null)
      setFormData({
        title: '',
        slug: '',
        excerpt: '',
        content: '',
        category: '',
        tags: '',
        image: '',
        author: 'Contenta Team',
        published: false,
        featured: false
      })
    } catch (error) {
      console.error('Error saving post:', error)
      alert('❌ Gabim gjatë ruajtjes!')
    }
  }

  const handleEdit = (post) => {
    setEditingPost(post)
    setFormData({
      title: post.title || '',
      slug: post.slug || '',
      excerpt: post.excerpt || '',
      content: post.content || '',
      category: post.category || '',
      tags: Array.isArray(post.tags) ? post.tags.join(', ') : '',
      image: post.image || '',
      author: post.author || 'Contenta Team',
      published: post.published || false,
      featured: post.featured || false
    })
    setShowEditor(true)
  }

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, 'blogPosts', id))
    } catch (error) {
      console.error('Error deleting post:', error)
    }
  }

  const handleTogglePublish = async (id, currentStatus) => {
    try {
      await updateDoc(doc(db, 'blogPosts', id), {
        published: !currentStatus,
        updatedAt: serverTimestamp()
      })
    } catch (error) {
      console.error('Error toggling publish:', error)
    }
  }

  const handleToggleFeatured = async (id, currentStatus) => {
    try {
      await updateDoc(doc(db, 'blogPosts', id), {
        featured: !currentStatus,
        updatedAt: serverTimestamp()
      })
    } catch (error) {
      console.error('Error toggling featured:', error)
    }
  }

  const openDeleteConfirm = (id) => {
    setConfirmModal({ isOpen: true, postId: id })
  }

  const closeDeleteConfirm = () => {
    setConfirmModal({ isOpen: false, postId: null })
  }

  const confirmDelete = () => {
    if (confirmModal.postId) {
      handleDelete(confirmModal.postId)
    }
  }

  const categories = [
    'Digital Marketing',
    'Social Media',
    'SEO',
    'Web Development',
    'Branding',
    'Content Marketing',
    'E-commerce',
    'Analytics',
    'Trends',
    'Tips & Tricks'
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-display font-bold text-gold-400 flex items-center">
            <FileText className="w-6 h-6 mr-2" />
            Blog CMS
          </h2>
          <p className="text-beige-300 text-sm mt-1">
            {posts.length} artikuj totale • {posts.filter(p => p.published).length} të publikuar
          </p>
        </div>
        <button
          onClick={() => {
            setShowEditor(true)
            setEditingPost(null)
          }}
          className="btn-primary"
        >
          <Plus className="w-4 h-4 mr-2" />
          Artikull i Ri
        </button>
      </div>

      {/* Posts Grid */}
      {!showEditor && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence>
            {posts.map((post) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="bg-bronze-900/30 backdrop-blur-sm border border-gold-500/20 rounded-2xl overflow-hidden hover:border-gold-500/40 transition-all group"
              >
                {/* Image */}
                {post.image && (
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    {post.featured && (
                      <div className="absolute top-2 right-2 px-2 py-1 bg-gold-500 text-white text-xs font-bold rounded-full">
                        ⭐ Featured
                      </div>
                    )}
                  </div>
                )}

                <div className="p-4">
                  {/* Category & Date */}
                  <div className="flex items-center justify-between text-xs text-beige-400 mb-2">
                    <span className="px-2 py-1 bg-gold-500/20 text-gold-400 rounded">
                      {post.category}
                    </span>
                    <span>{formatDate(post.createdAt, 'short')}</span>
                  </div>

                  {/* Title */}
                  <h3 className="text-cream font-bold text-lg mb-2 line-clamp-2 group-hover:text-gold-400 transition-colors">
                    {post.title}
                  </h3>

                  {/* Excerpt */}
                  <p className="text-beige-300 text-sm mb-3 line-clamp-2">
                    {post.excerpt}
                  </p>

                  {/* Tags */}
                  {post.tags && post.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {post.tags.slice(0, 3).map((tag, index) => (
                        <span
                          key={index}
                          className="text-xs px-2 py-1 bg-dark/50 text-beige-400 rounded"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Stats */}
                  <div className="flex items-center justify-between text-xs text-beige-400 mb-3 pb-3 border-b border-gold-500/10">
                    <span>👁️ {post.views || 0} views</span>
                    <span>⏱️ {post.readTime || 5} min lexim</span>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleTogglePublish(post.id, post.published)}
                        className={`p-2 rounded-lg transition-all ${
                          post.published
                            ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                            : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                        }`}
                        title={post.published ? 'Publikuar' : 'Draft'}
                      >
                        {post.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => handleToggleFeatured(post.id, post.featured)}
                        className={`p-2 rounded-lg transition-all ${
                          post.featured
                            ? 'bg-gold-500/20 text-gold-400 hover:bg-gold-500/30'
                            : 'bg-dark/50 text-beige-400 hover:bg-dark/70'
                        }`}
                        title="Featured"
                      >
                        ⭐
                      </button>
                    </div>

                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(post)}
                        className="p-2 hover:bg-blue-500/20 rounded-lg transition-all"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4 text-blue-400" />
                      </button>
                      <button
                        onClick={() => openDeleteConfirm(post.id)}
                        className="p-2 hover:bg-red-500/20 rounded-lg transition-all"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {posts.length === 0 && !showEditor && (
        <div className="text-center py-20 bg-bronze-900/20 rounded-2xl">
          <FileText className="w-16 h-16 text-beige-400 mx-auto mb-4 opacity-50" />
          <p className="text-beige-300">
            Nuk ka artikuj ende. Krijo të parën!
          </p>
        </div>
      )}

      {/* Editor */}
      <AnimatePresence>
        {showEditor && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-bronze-900/30 backdrop-blur-sm border border-gold-500/20 rounded-2xl p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-display font-bold text-gold-400">
                {editingPost ? 'Edito Artikullin' : 'Artikull i Ri'}
              </h3>
              <button
                onClick={() => {
                  setShowEditor(false)
                  setEditingPost(null)
                }}
                className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-red-400" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Title & Slug */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-beige-200 text-sm mb-2">Titulli *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    required
                    className="w-full px-4 py-3 bg-dark/50 border border-gold-500/30 rounded-xl text-cream placeholder-beige-400 focus:border-gold-400 focus:ring-2 focus:ring-gold-500/20 transition-all outline-none"
                    placeholder="Titulli i artikullit..."
                  />
                </div>
                <div>
                  <label className="block text-beige-200 text-sm mb-2">Slug (URL) *</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-dark/50 border border-gold-500/30 rounded-xl text-cream placeholder-beige-400 focus:border-gold-400 focus:ring-2 focus:ring-gold-500/20 transition-all outline-none"
                    placeholder="url-i-artikullit"
                  />
                </div>
              </div>

              {/* Excerpt */}
              <div>
                <label className="block text-beige-200 text-sm mb-2">Përmbledhje *</label>
                <textarea
                  value={formData.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  required
                  rows={3}
                  className="w-full px-4 py-3 bg-dark/50 border border-gold-500/30 rounded-xl text-cream placeholder-beige-400 focus:border-gold-400 focus:ring-2 focus:ring-gold-500/20 transition-all outline-none resize-none"
                  placeholder="Përmbledhje e shkurtër..."
                />
              </div>

              {/* Content */}
              <div>
                <label className="block text-beige-200 text-sm mb-2">Përmbajtja *</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  required
                  rows={15}
                  className="w-full px-4 py-3 bg-dark/50 border border-gold-500/30 rounded-xl text-cream placeholder-beige-400 focus:border-gold-400 focus:ring-2 focus:ring-gold-500/20 transition-all outline-none resize-none font-mono text-sm"
                  placeholder="Përmbajtja e plotë e artikullit..."
                />
                <p className="text-xs text-beige-400 mt-2">
                  💡 Tip: Përdor Markdown për formatim (# për tituj, **bold**, *italic*, etc.)
                </p>
              </div>

              {/* Category, Tags, Author */}
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-beige-200 text-sm mb-2">Kategoria *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-dark/50 border border-gold-500/30 rounded-xl text-cream focus:border-gold-400 focus:ring-2 focus:ring-gold-500/20 transition-all outline-none"
                  >
                    <option value="">Zgjidh kategorinë</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-beige-200 text-sm mb-2">Tags (comma separated)</label>
                  <input
                    type="text"
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    className="w-full px-4 py-3 bg-dark/50 border border-gold-500/30 rounded-xl text-cream placeholder-beige-400 focus:border-gold-400 focus:ring-2 focus:ring-gold-500/20 transition-all outline-none"
                    placeholder="marketing, seo, tips"
                  />
                </div>
                <div>
                  <label className="block text-beige-200 text-sm mb-2">Autori</label>
                  <input
                    type="text"
                    value={formData.author}
                    onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                    className="w-full px-4 py-3 bg-dark/50 border border-gold-500/30 rounded-xl text-cream placeholder-beige-400 focus:border-gold-400 focus:ring-2 focus:ring-gold-500/20 transition-all outline-none"
                    placeholder="Contenta Team"
                  />
                </div>
              </div>

              {/* Image URL */}
              <div>
                <label className="block text-beige-200 text-sm mb-2">URL e Imazhit</label>
                <input
                  type="url"
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  className="w-full px-4 py-3 bg-dark/50 border border-gold-500/30 rounded-xl text-cream placeholder-beige-400 focus:border-gold-400 focus:ring-2 focus:ring-gold-500/20 transition-all outline-none"
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              {/* Checkboxes */}
              <div className="flex items-center space-x-6">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.published}
                    onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                    className="w-4 h-4 rounded border-gold-500/30 text-gold-500 focus:ring-2 focus:ring-gold-500/20"
                  />
                  <span className="text-beige-200">Publiko menjëherë</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.featured}
                    onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                    className="w-4 h-4 rounded border-gold-500/30 text-gold-500 focus:ring-2 focus:ring-gold-500/20"
                  />
                  <span className="text-beige-200">⭐ Featured</span>
                </label>
              </div>

              {/* Submit Button */}
              <div className="flex space-x-3">
                <button type="submit" className="btn-primary flex-1">
                  <Save className="w-4 h-4 mr-2" />
                  {editingPost ? 'Ruaj Ndryshimet' : 'Publiko Artikullin'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditor(false)
                    setEditingPost(null)
                  }}
                  className="btn-secondary flex-1"
                >
                  Anulo
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirm Delete Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={closeDeleteConfirm}
        onConfirm={confirmDelete}
        title="Fshi Artikullin"
        message="Dëshiron ta fshish këtë artikull? Ky veprim nuk mund të zhbëhet."
        confirmText="Po, Fshije"
        cancelText="Jo, Anulo"
      />
    </div>
  )
}

export default BlogCMS
